import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldCheck } from "lucide-react";
import type { UserSettings } from "@/hooks/useUserSettings";

interface SecuritySettingsProps {
  settings: UserSettings;
  onToggleTwoFactor: () => Promise<void>;
  isLoading?: boolean;
}

export function SecuritySettings({
  settings,
  onToggleTwoFactor,
  isLoading = false,
}: SecuritySettingsProps) {
  const handleToggleTwoFactor = async () => {
    try {
      await onToggleTwoFactor();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Two-Factor Authentication</Label>
          <p className="text-sm text-muted-foreground">
            Add an extra layer of security to your account
          </p>
        </div>
        <Switch
          checked={settings.two_factor_enabled}
          onCheckedChange={handleToggleTwoFactor}
          disabled={isLoading}
        />
      </div>

      {settings.two_factor_enabled && (
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Two-factor authentication is enabled</AlertTitle>
          <AlertDescription>
            Your account is protected with an additional layer of security.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-0.5">
        <Label>Phone Verification</Label>
        <p className="text-sm text-muted-foreground">
          {settings.phone_verified
            ? "Your phone number is verified"
            : "Verify your phone number for additional security"}
        </p>
        {!settings.phone_verified && (
          <Button variant="outline" className="mt-2" disabled={isLoading}>
            Verify Phone Number
          </Button>
        )}
      </div>

      <div className="space-y-0.5">
        <Label>Recent Activity</Label>
        <p className="text-sm text-muted-foreground">
          Last password change:{" "}
          {settings.last_password_change
            ? new Date(settings.last_password_change).toLocaleDateString()
            : "Never"}
        </p>
      </div>
    </div>
  );
} 